import type {
  TypedSchemaInput,
  TypedSchemaOutput,
} from "@/utils/typeUtils"
import { toTypedSchema } from "@vee-validate/zod"
import { useForm } from "vee-validate"
import { z } from "zod"
import {
  emailAddressSchema,
  requiredTrimmedString,
} from "@/validation/sharedStringFieldSchemas"

export const merchantGroupAdminEmailSchema =
  emailAddressSchema("Please enter an email address")

const merchantGroupFormSchema = z.object({
  name: requiredTrimmedString(
    "Please enter a merchant group name"
  ),
  merchants: z.array(
    z.object({
      id: z.number(),
      name: z.string(),
      mid: z.string().nullable(),
      currency: z.string(),
    })
  ),
  admins: z
    .array(
      z.object({
        email: merchantGroupAdminEmailSchema,
      })
    )
    // Check already added email fields are unique when they are edited inline
    .superRefine((admins, ctx) => {
      const addedEmails = new Set()
      for (let i = 0; i < admins.length; i++) {
        const email = admins[i].email
        if (addedEmails.has(email)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message:
              "Email already added. Please enter a differnt one",
            path: [i, "email"],
          })
        }
        addedEmails.add(email)
      }
    }),
})

export type MerchantGroupFormInput = TypedSchemaInput<
  typeof merchantGroupFormSchema
>

export type MerchantGroupFormMerchant = NonNullable<
  MerchantGroupFormInput["merchants"]
>[number]

export type MerchantGroupFormAdmin = NonNullable<
  MerchantGroupFormInput["admins"]
>[number]

export type MerchantGroupFormOutput = TypedSchemaOutput<
  typeof merchantGroupFormSchema
>

// It is not appropriate to use reactive initial values here.
// If initialValues is a ref, vee-validate will update the value of any untouched, existing fields on change.
// However in the case of an array field, such as merchants, as the initial array has length 0, the fields "merchant[0]", `merchant[1]` etc
// do not exist upon form creation, so are not updated when initialValues changes to have an array of positive length. This produces hard to reason with behaviour.
// As such it is a better idea to call `resetForm` to change initial values.
export const useMerchantGroupForm = () =>
  useForm({
    validationSchema: toTypedSchema(
      merchantGroupFormSchema
    ),
    initialValues: {
      name: "",
      merchants: [],
      admins: [],
    },
  })
